import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Search, ChevronLeft, ChevronRight, Ban, CheckCircle, UserPlus, UserMinus } from 'lucide-react';

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignForm, setAssignForm] = useState({ user_email: '', series_id: '' });
  const [allSeries, setAllSeries] = useState([]);
  const [assignMode, setAssignMode] = useState('assign');

  useEffect(() => { loadUsers(); }, [page, limit, search]);
  useEffect(() => { loadSeries(); }, []);

  async function loadUsers() {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (search) params.append('search', search);
      const { data } = await api.get(`/admin/users?${params}`);
      setUsers(data.items || []);
      setTotalPages(data.pages || 1);
    } catch {} finally { setLoading(false); }
  }

  async function loadSeries() {
    try { const { data } = await api.get('/admin/test-series?limit=100'); setAllSeries(data.items || []); } catch {}
  }

  async function toggleBlock(userId) {
    try {
      const { data } = await api.put(`/admin/users/${userId}/toggle-block`);
      toast.success(data.is_blocked ? 'User blocked' : 'User unblocked');
      loadUsers();
    } catch { toast.error('Failed'); }
  }

  async function handleAssign() {
    if (!assignForm.user_email || !assignForm.series_id) { toast.error('Fill all fields'); return; }
    try {
      if (assignMode === 'assign') {
        await api.post('/admin/users/assign-series', assignForm);
        toast.success('Access granted');
      } else {
        await api.post('/admin/users/revoke-series', assignForm);
        toast.success('Access revoked');
      }
      setAssignDialogOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  }

  return (
    <div data-testid="user-management">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-['Outfit'] text-2xl font-semibold text-[#0A0A0A]">User Management</h1>
        <div className="flex gap-2">
          <Button variant="outline" className="text-green-600" onClick={() => { setAssignMode('assign'); setAssignDialogOpen(true); }} data-testid="assign-series-btn">
            <UserPlus size={14} className="mr-1" /> Assign Series
          </Button>
          <Button variant="outline" className="text-red-500" onClick={() => { setAssignMode('revoke'); setAssignDialogOpen(true); }} data-testid="revoke-series-btn">
            <UserMinus size={14} className="mr-1" /> Revoke Series
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-4 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search users..." className="pl-9" data-testid="user-search-input" />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-[#52525B]">Rows:</span>
          <Select value={String(limit)} onValueChange={v => { setLimit(Number(v)); setPage(1); }}>
            <SelectTrigger className="w-20 h-8"><SelectValue /></SelectTrigger>
            <SelectContent>{[10, 20, 30, 50, 100].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <Card className="border border-zinc-200 bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Registered</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8">Loading...</TableCell></TableRow>
            ) : users.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-[#52525B]">No users found</TableCell></TableRow>
            ) : users.map(u => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium">{u.name || '-'}</TableCell>
                <TableCell className="text-sm">{u.email}</TableCell>
                <TableCell className="text-sm text-[#52525B]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</TableCell>
                <TableCell>
                  <Badge className={u.is_blocked ? 'bg-red-100 text-red-700 border-0' : 'bg-green-100 text-green-700 border-0'}>
                    {u.is_blocked ? 'Blocked' : 'Active'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Button variant="ghost" size="sm" onClick={() => toggleBlock(u.id)}
                    className={u.is_blocked ? 'text-green-600' : 'text-red-500'}
                    data-testid={`toggle-block-${u.id}`}>
                    {u.is_blocked ? <CheckCircle size={14} /> : <Ban size={14} />}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <div className="flex justify-end mt-4 gap-2 items-center">
        <span className="text-sm text-[#52525B]">Page {page}/{totalPages}</span>
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft size={14} /></Button>
        <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight size={14} /></Button>
      </div>

      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">{assignMode === 'assign' ? 'Assign Test Series' : 'Revoke Test Series'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>User Email</Label><Input value={assignForm.user_email} onChange={e => setAssignForm({ ...assignForm, user_email: e.target.value })} placeholder="user@email.com" data-testid="assign-email-input" /></div>
            <div><Label>Test Series</Label>
              <Select value={assignForm.series_id} onValueChange={v => setAssignForm({ ...assignForm, series_id: v })}>
                <SelectTrigger data-testid="assign-series-select"><SelectValue placeholder="Select series" /></SelectTrigger>
                <SelectContent>{allSeries.map(s => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
            <Button className={assignMode === 'assign' ? 'bg-[#7551e8] hover:bg-[#5e3dc7] text-white' : 'bg-red-500 hover:bg-red-600 text-white'}
              onClick={handleAssign} data-testid="confirm-assign-btn">
              {assignMode === 'assign' ? 'Grant Access' : 'Revoke Access'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
